import React, {useEffect, useMemo, useState} from "react";
import {Badge, Checkbox, Col, Row} from "antd";
import BookCover from "@components/book-cover";
import {RightOutlined} from "@ant-design/icons";
import {IssueRenderProps} from "@components/grid/IssueGrid";
import {DiamondIcon, FlameIcon, UserIcon} from "@components/icons/Icons";
import {formatGrade, formatPercent} from "@lib/format";
import moment from "moment";
import {FmvPrice} from "@components/fmv-price";
import {KeyIssueBadge} from "@components/key-issue-badge";
import {isMatch} from "underscore";
import {Issue} from "@mwServices/Issue";

interface MyCollectionGridItemProps extends IssueRenderProps {
    showEditMultiple?: boolean;
    isSelected?: boolean;
    toggleSelected: (collectionId: number) => void;
    showValueRibbon?: boolean;
    showDesktopView?: boolean;
    windowDimensions: any;
}

const getItemProps = (item: Issue) => {
    return {
        collection_item_id: item.collection_item_id,
        title: item.title,
        series_title: item.series_title,
        is_variant: item.is_variant,
        total_in_grade: item.total_in_grade,
        issue_number: item.issue_number,
        date: item.date,
        is_key: item.is_key,
        is_hot: item.is_hot,
        is_rare: item.is_rare,
        graded_or_raw: item.graded_or_raw,
        user_value: item.user_value,
        trend_percent: item.trend_percent,
        stale: item.stale,
    }
}

const MyCollectionGridItem = ({
                                  item,
                                  onClick,
                                  showEditMultiple,
                                  isSelected,
                                  toggleSelected,
                                  showValueRibbon,
                                  showDesktopView,
                                  windowDimensions
}: MyCollectionGridItemProps) => {

    const [cachedItemProps, setCachedItemProps] = useState(getItemProps(item));
    useEffect(() => {
        const newItemProps = getItemProps(item);
        if(!isMatch(cachedItemProps, newItemProps)) {
            setCachedItemProps(newItemProps);
        }
    }, [item]);

    const header = useMemo(() => {
        return (
            <Row justify="space-between">
                <span className="grid-item-cbx-col">
                    {showEditMultiple && item.total_in_grade < 2 && (
                        <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelected(item.collection_item_id)}
                        />
                    )}
                </span>
            </Row>
        )
    }, [cachedItemProps, showEditMultiple, isSelected]);

    const footer = useMemo(() => {
        const issueDate =moment(item.date).toDate();

        return (
            <Row justify="center">
                <Col span={24}>
                    <Row
                        className="issue-grid-book-cover-value-white"
                        style={{ borderBottomWidth: showValueRibbon ? 0 : 1 }}
                    >
                        <Col span={12}>
                            <span className="issueNumber">{`${item.issue_number}`}</span>
                            <span className="issueYear">({issueDate.getFullYear()})</span>
                        </Col>
                        <Col span={12} className="valueBadges">
                            {item.is_key && (<KeyIssueBadge keyInfo={item.key_info} windowDimensions={windowDimensions} isLargeScreen={showDesktopView} />)}
                            {item.is_hot && (<div className="valueBadge hotIssue isActive"><FlameIcon /></div>)}
                            {item.is_rare && (<div className="valueBadge rareIssue isActive"><DiamondIcon /></div>)}
                        </Col>
                    </Row>
                    {showValueRibbon && (
                        <Row className={
                            'issue-grid-book-cover-value ' +
                            ((item.graded_or_raw.toUpperCase() !== 'RAW') ? ' blue ' : ' grey ') +
                            ((item.user_value > 0) ? ' black ' : '')
                        }>
                            <Col span={18}  >
                                    <span className="issueGrade">
                                        {item.graded_by ? item.graded_by.toUpperCase() : item.graded_or_raw.toUpperCase()} {formatGrade(item.grade)} |
                                    </span>
                                <FmvPrice
                                    prefix={
                                        (item.is_most_common_value && !(item.user_value > 0) && item.most_common_grade) ?
                                            '('+formatGrade(item.most_common_grade)+') ' :
                                            null
                                    }
                                    value={item.value}
                                    stale={item.stale}
                                />
                            </Col>
                            {(item.user_value > 0) ? (
                                <Col span={6} className="valueChange">
                                    <UserIcon className="grid-item-user-icon left" />
                                </Col>
                            ) : (
                                <>
                                    {item.trend_percent !== null && item.trend_percent !== 0 && (
                                        <Col span={6} className={
                                            "valueChange " +
                                            (item.trend_percent && item.trend_percent >= 0 ? 'up' : 'down') +
                                            (item.stale ? ' stale' : '')
                                        }>
                                            {formatPercent(item.trend_percent, true)}
                                        </Col>
                                    )}
                                </>
                            )}
                        </Row>
                    )}
                </Col>
            </Row>
        );
    }, [cachedItemProps, showValueRibbon, showDesktopView]);

    return (
        <>
            <div className="covr-grid-header" >
                {header}
            </div>
            <div className="covr-grid-content">
                <BookCover issue={item} onClick={onClick} text={item.series_title} />
            </div>
            <div className="covr-grid-footer">
                {footer}
                <div className="select-arrow" />
            </div>
        </>
    );
};

export default MyCollectionGridItem;